import React, { useState, useEffect } from 'react';
import { 
  PlusIcon, 
  SearchIcon, 
  FilterIcon, 
  TestTubeIcon,
  UserIcon,
  AlertTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  EyeIcon,
  FileTextIcon,
  BeakerIcon,
  X
} from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'react-hot-toast';

interface LabOrder {
  id: string;
  patientId: string;
  visitId?: string;
  testName: string;
  testType: string;
  testCategory?: string;
  orderedBy: string;
  orderedDate: string;
  status: 'ORDERED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
  priority?: string;
  specimenType?: string;
  collectionDate?: string;
  results?: any;
  resultDate?: string;
  normalRange?: any;
  interpretation?: string;
  notes?: string;
  patient: {
    user: {
      firstName: string;
      lastName: string;
      email: string;
    };
  };
  visit?: {
    doctor: {
      user: {
        firstName: string;
        lastName: string;
      };
    };
  };
}

interface Patient {
  id: string;
  user: {
    firstName: string;
    lastName: string;
    email: string;
  };
}

interface Doctor {
  id: string;
  user: {
    firstName: string;
    lastName: string;
    email: string;
  };
}

interface Visit {
  id: string;
  visitType: string;
  visitDate: string;
  diagnosis: string;
  doctor: {
    user: {
      firstName: string;
      lastName: string;
    };
  };
}

const LabOrdersPage: React.FC = () => {
  const [labOrders, setLabOrders] = useState<LabOrder[]>([]);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [visits, setVisits] = useState<Visit[]>([]);
  const [selectedPatientId, setSelectedPatientId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [testTypeFilter, setTestTypeFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedLabOrder, setSelectedLabOrder] = useState<LabOrder | null>(null);

  useEffect(() => {
    fetchLabOrders();
    fetchPatients();
    fetchDoctors();
  }, []);

  const fetchLabOrders = async () => {
    try {
      setLoading(true);
      const response = await api.get('/lab-orders');
      setLabOrders(response.data.labOrders || []);
    } catch (error) {
      console.error('Error fetching lab orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPatients = async () => {
    try {
      const response = await api.get('/patients');
      setPatients(response.data.data?.patients || []);
    } catch (error) {
      console.error('Error fetching patients:', error);
    }
  };

  const fetchDoctors = async () => {
    try {
      const response = await api.get('/doctors');
      setDoctors(response.data.data?.doctors || []);
    } catch (error) {
      console.error('Error fetching doctors:', error);
    }
  };

  const fetchVisitsForPatient = async (patientId: string) => {
    try {
      const response = await api.get(`/visits?patientId=${patientId}`);
      setVisits(response.data.visits || []);
    } catch (error) {
      console.error('Error fetching visits for patient:', error);
      setVisits([]);
    }
  };

  const filteredLabOrders = labOrders.filter(labOrder => {
    const matchesSearch = 
      labOrder.testName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      labOrder.patient.user.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      labOrder.patient.user.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      labOrder.specimenType?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = !statusFilter || labOrder.status === statusFilter;
    const matchesTestType = !testTypeFilter || labOrder.testType === testTypeFilter;
    const matchesPriority = !priorityFilter || labOrder.priority === priorityFilter;
    
    return matchesSearch && matchesStatus && matchesTestType && matchesPriority;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return 'bg-green-100 text-green-800';
      case 'IN_PROGRESS':
        return 'bg-blue-100 text-blue-800';
      case 'ORDERED':
        return 'bg-yellow-100 text-yellow-800';
      case 'CANCELLED':
        return 'bg-red-100 text-red-800';
      case 'RESULT_READY':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'EMERGENCY':
        return 'bg-red-100 text-red-800';
      case 'URGENT':
        return 'bg-orange-100 text-orange-800';
      case 'STAT':
        return 'bg-purple-100 text-purple-800';
      case 'ROUTINE':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTestTypeColor = (testType: string) => {
    switch (testType) {
      case 'HEMATOLOGY':
        return 'bg-red-100 text-red-800';
      case 'BIOCHEMISTRY':
        return 'bg-blue-100 text-blue-800';
      case 'MICROBIOLOGY':
        return 'bg-green-100 text-green-800';
      case 'RADIOLOGY':
        return 'bg-purple-100 text-purple-800';
      case 'PATHOLOGY':
        return 'bg-orange-100 text-orange-800';
      case 'CARDIOLOGY':
        return 'bg-pink-100 text-pink-800';
      case 'NEUROLOGY':
        return 'bg-indigo-100 text-indigo-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleCollectSpecimen = async (labOrderId: string) => {
    try {
      await api.post(`/lab-orders/${labOrderId}/collect`, {
        collectedBy: 'Current User',
        collectionNotes: 'Specimen collected successfully'
      });
      fetchLabOrders(); // Refresh the list
    } catch (error) {
      console.error('Error collecting specimen:', error);
    }
  };

  const handleCompleteLabOrder = async (labOrderId: string) => {
    try {
      await api.post(`/lab-orders/${labOrderId}/complete`, {
        results: { sample: 'Normal' },
        interpretation: 'Results within normal range',
        completedBy: 'Current User'
      });
      fetchLabOrders(); // Refresh the list
    } catch (error) {
      console.error('Error completing lab order:', error);
    }
  };

  const handleCloseModal = () => {
    setShowCreateModal(false);
    setSelectedPatientId('');
    setVisits([]);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Lab Orders</h1>
            <p className="text-gray-600">Manage laboratory tests and results</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 flex items-center space-x-2"
          >
            <PlusIcon className="h-5 w-5" />
            <span>New Lab Order</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {/* Search */}
          <div className="relative">
            <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type="text"
              placeholder="Search tests, patients..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="">All Statuses</option>
            <option value="ORDERED">Ordered</option>
            <option value="IN_PROGRESS">In Progress</option>
            <option value="COMPLETED">Completed</option>
            <option value="CANCELLED">Cancelled</option>
            <option value="RESULT_READY">Result Ready</option>
          </select>

          {/* Test Type Filter */}
          <select
            value={testTypeFilter}
            onChange={(e) => setTestTypeFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="">All Test Types</option>
            <option value="HEMATOLOGY">Hematology</option>
            <option value="BIOCHEMISTRY">Biochemistry</option>
            <option value="MICROBIOLOGY">Microbiology</option>
            <option value="RADIOLOGY">Radiology</option>
            <option value="PATHOLOGY">Pathology</option>
            <option value="CARDIOLOGY">Cardiology</option>
            <option value="NEUROLOGY">Neurology</option>
            <option value="OTHER">Other</option>
          </select>

          {/* Priority Filter */}
          <select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="">All Priorities</option>
            <option value="ROUTINE">Routine</option>
            <option value="URGENT">Urgent</option>
            <option value="EMERGENCY">Emergency</option>
            <option value="STAT">STAT</option>
          </select>

          {/* Clear Filters */}
          <button
            onClick={() => {
              setSearchTerm('');
              setStatusFilter('');
              setTestTypeFilter('');
              setPriorityFilter('');
            }}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <TestTubeIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Orders</p>
              <p className="text-2xl font-bold text-gray-900">{labOrders.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircleIcon className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Completed</p>
              <p className="text-2xl font-bold text-gray-900">
                {labOrders.filter(l => l.status === 'COMPLETED').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <ClockIcon className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Pending</p>
              <p className="text-2xl font-bold text-gray-900">
                {labOrders.filter(l => ['ORDERED', 'IN_PROGRESS'].includes(l.status)).length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 rounded-lg">
              <AlertTriangleIcon className="h-6 w-6 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Critical</p>
              <p className="text-2xl font-bold text-gray-900">
                {labOrders.filter(l => l.priority === 'EMERGENCY' || l.priority === 'STAT').length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Lab Orders Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Patient
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Test
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ordered Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Priority
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredLabOrders.map((labOrder) => (
                <tr key={labOrder.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                          <UserIcon className="h-5 w-5 text-primary-600" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {labOrder.patient.user.firstName} {labOrder.patient.user.lastName}
                        </div>
                        <div className="text-sm text-gray-500">
                          {labOrder.patient.user.email}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{labOrder.testName}</div>
                      {labOrder.specimenType && (
                        <div className="text-sm text-gray-500">{labOrder.specimenType}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTestTypeColor(labOrder.testType)}`}>
                      {labOrder.testType}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {formatDate(labOrder.orderedDate)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(labOrder.priority || 'ROUTINE')}`}>
                      {labOrder.priority || 'ROUTINE'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(labOrder.status)}`}>
                      {labOrder.status.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setSelectedLabOrder(labOrder)}
                        className="text-primary-600 hover:text-primary-900"
                        title="View Details"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </button>
                      {labOrder.status === 'ORDERED' && (
                        <button
                          onClick={() => handleCollectSpecimen(labOrder.id)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Collect Specimen"
                        >
                          <BeakerIcon className="h-4 w-4" />
                        </button>
                      )}
                      {labOrder.status === 'IN_PROGRESS' && (
                        <button
                          onClick={() => handleCompleteLabOrder(labOrder.id)}
                          className="text-green-600 hover:text-green-900"
                          title="Complete Order"
                        >
                          <CheckCircleIcon className="h-4 w-4" />
                        </button>
                      )}
                      {labOrder.status === 'COMPLETED' && (
                        <button
                          className="text-purple-600 hover:text-purple-900"
                          title="View Results"
                        >
                          <FileTextIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredLabOrders.length === 0 && (
          <div className="text-center py-12">
            <TestTubeIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No lab orders found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || statusFilter || testTypeFilter || priorityFilter 
                ? 'Try adjusting your filters.' 
                : 'Get started by creating a new lab order.'}
            </p>
            {!searchTerm && !statusFilter && !testTypeFilter && !priorityFilter && (
              <div className="mt-6">
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
                >
                  <PlusIcon className="h-5 w-5 mr-2" />
                  New Lab Order
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create Lab Order Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Create New Lab Order</h3>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              
              <form onSubmit={async (e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                const labOrderData = {
                  patientId: formData.get('patientId') as string,
                  visitId: formData.get('visitId') as string || undefined,
                  testName: formData.get('testName') as string,
                  testType: formData.get('testType') as 'BLOOD' | 'URINE' | 'STOOL' | 'IMAGING' | 'CULTURE' | 'OTHER',
                  priority: formData.get('priority') as 'ROUTINE' | 'URGENT' | 'STAT',
                  orderedDate: formData.get('orderedDate') as string,
                  scheduledDate: formData.get('scheduledDate') as string || undefined,
                  instructions: formData.get('instructions') as string,
                  notes: formData.get('notes') as string || undefined,
                  status: 'ORDERED' as const
                };

                try {
                  await api.post('/lab-orders', labOrderData);
                  toast.success('Lab order created successfully');
                  handleCloseModal();
                  fetchLabOrders();
                } catch (error) {
                  console.error('Error creating lab order:', error);
                  toast.error('Failed to create lab order');
                }
              }}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Patient Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Patient *
                    </label>
                    <select
                      name="patientId"
                      required
                      value={selectedPatientId}
                      onChange={(e) => {
                        setSelectedPatientId(e.target.value);
                        if (e.target.value) {
                          fetchVisitsForPatient(e.target.value);
                        } else {
                          setVisits([]);
                        }
                      }}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">Select a patient</option>
                      {patients.map((patient) => (
                        <option key={patient.id} value={patient.id}>
                          {patient.user.firstName} {patient.user.lastName} - {patient.user.email}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Visit Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Visit (Optional)
                    </label>
                    <select
                      name="visitId"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">Select a visit</option>
                      {visits.map((visit) => (
                        <option key={visit.id} value={visit.id}>
                          {visit.visitType} - {formatDate(visit.visitDate)} - {visit.diagnosis}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Test Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Test Name *
                    </label>
                    <input
                      type="text"
                      name="testName"
                      required
                      placeholder="e.g., Complete Blood Count, Lipid Panel"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  {/* Test Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Test Type *
                    </label>
                    <select
                      name="testType"
                      required
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">Select type</option>
                      <option value="BLOOD">Blood</option>
                      <option value="URINE">Urine</option>
                      <option value="STOOL">Stool</option>
                      <option value="IMAGING">Imaging</option>
                      <option value="CULTURE">Culture</option>
                      <option value="OTHER">Other</option>
                    </select>
                  </div>

                  {/* Priority */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Priority *
                    </label>
                    <select
                      name="priority"
                      required
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="ROUTINE">Routine</option>
                      <option value="URGENT">Urgent</option>
                      <option value="STAT">STAT</option>
                    </select>
                  </div>

                  {/* Ordered Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Ordered Date *
                    </label>
                    <input
                      type="date"
                      name="orderedDate"
                      required
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  {/* Scheduled Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Scheduled Date
                    </label>
                    <input
                      type="datetime-local"
                      name="scheduledDate"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>

                {/* Instructions */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Instructions *
                  </label>
                  <textarea
                    name="instructions"
                    required
                    rows={3}
                    placeholder="e.g., Fasting required for 12 hours, Collect first morning urine..."
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                {/* Notes */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Additional Notes
                  </label>
                  <textarea
                    name="notes"
                    rows={3}
                    placeholder="Any additional notes about the lab order..."
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                {/* Form Actions */}
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
                  >
                    Create Lab Order
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Lab Order Details Modal */}
      {selectedLabOrder && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900">Lab Order Details</h3>
              <div className="mt-4 text-sm text-gray-600 space-y-2">
                <p><strong>Patient:</strong> {selectedLabOrder.patient.user.firstName} {selectedLabOrder.patient.user.lastName}</p>
                <p><strong>Test:</strong> {selectedLabOrder.testName}</p>
                <p><strong>Type:</strong> {selectedLabOrder.testType}</p>
                <p><strong>Priority:</strong> {selectedLabOrder.priority}</p>
                <p><strong>Status:</strong> {selectedLabOrder.status}</p>
                <p><strong>Ordered:</strong> {formatDate(selectedLabOrder.orderedDate)}</p>
                {selectedLabOrder.collectionDate && <p><strong>Collected:</strong> {formatDate(selectedLabOrder.collectionDate)}</p>}
                {selectedLabOrder.resultDate && <p><strong>Results:</strong> {formatDate(selectedLabOrder.resultDate)}</p>}
                {selectedLabOrder.specimenType && <p><strong>Specimen:</strong> {selectedLabOrder.specimenType}</p>}
                {selectedLabOrder.notes && <p><strong>Notes:</strong> {selectedLabOrder.notes}</p>}
              </div>
              <div className="items-center px-4 py-3">
                <button
                  onClick={() => setSelectedLabOrder(null)}
                  className="px-4 py-2 bg-primary-600 text-white text-base font-medium rounded-md w-full shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-300"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LabOrdersPage; 